import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../core/Auth'
import { 
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier
} from 'firebase/auth'
import { auth } from '../../../../firebase'
import { logger } from '../../../utils/logger'

// Check if we're running on localhost (for development testing)
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

export function TwoFactorVerification() {
  const { mfaResolver, setMfaResolver, mfaVerificationId, setMfaVerificationId } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [codeSent, setCodeSent] = useState(false)
  const [selectedHintIndex, setSelectedHintIndex] = useState(0)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const recaptchaContainerRef = useRef<HTMLDivElement>(null)
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null)

  // Redirect if no MFA resolver is present
  useEffect(() => {
    if (!mfaResolver) {
      navigate('/auth/login')
    }
  }, [mfaResolver, navigate])

  // Initialize reCAPTCHA when component mounts
  // Note: On localhost, Firebase phone auth has limitations. For local testing,
  // use test phone numbers configured in Firebase Console.
  useEffect(() => {
    if (mfaResolver && recaptchaContainerRef.current && !recaptchaVerifierRef.current) {
      try {
        // Enable app verification disabled for testing on localhost only
        // This allows using test phone numbers configured in Firebase Console
        if (isLocalhost) {
          // @ts-ignore - This property exists but TypeScript doesn't know about it
          auth.settings.appVerificationDisabledForTesting = true
          logger.info('Localhost detected - app verification disabled for testing', {
            source: 'TwoFactorVerification',
            module: 'Authentication',
            action: '2FA_LOCALHOST_MODE'
          })
        }

        recaptchaVerifierRef.current = new RecaptchaVerifier(
          auth,
          recaptchaContainerRef.current,
          {
            size: 'invisible',
            callback: () => {
              // reCAPTCHA solved
            }
          }
        )
      } catch (error) {
        console.error('Error initializing reCAPTCHA:', error)
      }
    }

    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear()
        recaptchaVerifierRef.current = null
      }
    }
  }, [mfaResolver])

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace to go to previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    const newCode = [...code]
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i]
    }
    setCode(newCode)
    // Focus last filled input or next empty
    const lastIndex = Math.min(pastedData.length, 5)
    inputRefs.current[lastIndex]?.focus()
  }

  const sendVerificationCode = async () => {
    if (!mfaResolver || !recaptchaVerifierRef.current) {
      setError('Session expired. Please login again.')
      return
    }

    setSendingCode(true)
    setError(null)

    try {
      const phoneInfoOptions = {
        multiFactorHint: mfaResolver.hints[selectedHintIndex],
        session: mfaResolver.session
      }

      const phoneAuthProvider = new PhoneAuthProvider(auth)
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions, 
        recaptchaVerifierRef.current
      )

      setMfaVerificationId(verificationId)
      setCodeSent(true)

      logger.info('SMS verification code sent', {
        source: 'TwoFactorVerification.sendVerificationCode',
        module: 'Authentication',
        action: '2FA_SMS_SENT'
      })
    } catch (error: any) {
      logger.error('Failed to send SMS verification code', error, {
        source: 'TwoFactorVerification.sendVerificationCode',
        module: 'Authentication',
        action: '2FA_SMS_SEND_FAILURE',
        errorCode: error.code
      })

      // Reset reCAPTCHA on error
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear()
        recaptchaVerifierRef.current = new RecaptchaVerifier(
          auth,
          recaptchaContainerRef.current!,
          { size: 'invisible' }
        )
      }

      if (error.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later.')
      } else {
        setError('Failed to send verification code. Please try again.')
      }
    } finally {
      setSendingCode(false)
    }
  }

  const handleVerify = async () => {
    if (!mfaResolver || !mfaVerificationId) {
      setError('Session expired. Please login again.')
      return
    }

    const smsCode = code.join('')
    if (smsCode.length !== 6) {
      setError('Please enter the complete 6-digit code')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Create credential from verification ID and code
      const cred = PhoneAuthProvider.credential(mfaVerificationId, smsCode)
      
      // Create multi-factor assertion
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)

      // Complete sign-in with the assertion
      await mfaResolver.resolveSignIn(multiFactorAssertion)

      logger.info('2FA verification successful', {
        source: 'TwoFactorVerification.handleVerify',
        module: 'Authentication',
        action: '2FA_VERIFICATION_SUCCESS'
      })

      // Clear the resolver and verification ID
      setMfaResolver(null)
      setMfaVerificationId(null)

      // Reload to trigger auth state change
      window.location.reload()
    } catch (error: any) {
      logger.error('2FA verification failed', error, {
        source: 'TwoFactorVerification.handleVerify',
        module: 'Authentication',
        action: '2FA_VERIFICATION_FAILURE',
        errorCode: error.code
      })

      if (error.code === 'auth/invalid-verification-code') {
        setError('Invalid verification code. Please try again.')
      } else if (error.code === 'auth/code-expired') {
        setError('Verification code has expired. Please request a new one.')
        setCodeSent(false)
        setCode(['', '', '', '', '', ''])
      } else {
        setError('Verification failed. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setMfaResolver(null)
    setMfaVerificationId(null)
    navigate('/auth/login')
  }

  if (!mfaResolver) {
    return null
  }

  // Get phone hints
  const phoneHints = mfaResolver.hints.filter(
    hint => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID
  )

  return (
    <div className='w-100'>
      {/* reCAPTCHA container (invisible) */}
      <div ref={recaptchaContainerRef} id='recaptcha-container-mfa'></div>

      {/* Header */}
      <h1 className='wc-auth__heading'>Two-factor authentication</h1>
      <p className='wc-auth__subtitle'>
        {codeSent
          ? 'Enter the 6-digit code sent to your phone'
          : 'Verify your identity with a code sent to your phone'}
      </p>

      {/* Error Alert */}
      {error && (
        <div className='wc-auth__alert wc-auth__alert--danger' role='alert'>
          {error}
        </div>
      )}

      {!codeSent ? (
        <>
          {/* Phone Selection (if multiple) */}
          {phoneHints.length > 1 && (
            <div className='wc-auth__group'>
              <label className='wc-auth__label' htmlFor='wc-auth-2fa-phone'>
                Phone number
              </label>
              <select
                id='wc-auth-2fa-phone'
                className='wc-auth__input'
                value={selectedHintIndex}
                onChange={e => setSelectedHintIndex(Number(e.target.value))}
              >
                {phoneHints.map((hint, index) => (
                  <option key={hint.uid} value={index}>
                    {hint.displayName || `Phone ${index + 1}`} ({(hint as any).phoneNumber || 'Hidden'})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Info Box */}
          <div className='wc-auth__alert wc-auth__alert--info'>
            We'll send a verification code to your registered phone number ending in{' '}
            <strong>{(phoneHints[selectedHintIndex] as any)?.phoneNumber || '****'}</strong>
          </div>

          {/* Send Code Button */}
          <button
            type='button'
            onClick={sendVerificationCode}
            className='wc-auth__submit'
            disabled={sendingCode}
          >
            {!sendingCode && <span>Send verification code</span>}
            {sendingCode && (
              <span style={{display: 'inline-flex', alignItems: 'center'}}>
                Sending…
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>

          <button type='button' onClick={handleCancel} className='wc-auth__ghost'>
            Back to login
          </button>
        </>
      ) : (
        <>
          {/* Code Input */}
          <div className='wc-auth__group'>
            <label className='wc-auth__label' style={{textAlign: 'center'}}>
              Verification code
            </label>
            <div className='wc-auth__code' onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type='text'
                  inputMode='numeric'
                  maxLength={1}
                  value={digit}
                  onChange={e => handleCodeChange(index, e.target.value)}
                  onKeyDown={e => handleKeyDown(index, e)}
                  autoFocus={index === 0}
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <button
            type='button'
            onClick={handleVerify}
            className='wc-auth__submit'
            disabled={loading || code.join('').length !== 6}
          >
            {!loading && <span>Verify</span>}
            {loading && (
              <span style={{display: 'inline-flex', alignItems: 'center'}}>
                Verifying…
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>

          <div className='wc-auth__or'>
            <span>or</span>
          </div>

          <button
            type='button'
            onClick={() => {
              setCodeSent(false)
              setCode(['', '', '', '', '', ''])
            }}
            className='wc-auth__secondary'
            disabled={sendingCode}
          >
            Resend code
          </button>

          <button type='button' onClick={handleCancel} className='wc-auth__ghost'>
            Back to login
          </button>
        </>
      )}
    </div>
  )
}
